import React, { useState } from "react";
import { Fragment } from "react";
import { HiArrowsUpDown, HiOutlineCheck, HiPlus } from "react-icons/hi2";
import { Listbox, Transition } from "@headlessui/react";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const SelectComponent = ({
  title,
  selected,
  setSelected,
  dataArr,
  withAvatar,
  required = false,
  allowDynamicAdd = false, // Feature flag for dynamic adding
  onAddOption, // New callback prop for adding options
  onAddNew,
  addLabel,
}) => {
  const [newOption, setNewOption] = useState("");

  // Regular expression to allow only alphanumeric, -, _, and .
  const validInputRegex = /^[a-zA-Z0-9-_. ]*$/;

  const handleInputChange = (e) => {
    const value = e.target.value;
    // Only update state if the input matches the allowed characters
    if (validInputRegex.test(value)) {
      setNewOption(value);
    }
  };

  const handleAddOption = (e) => {
    if (e.key === "Enter" && newOption.trim() !== "") {
      // Double-check the input against the regex
      if (!validInputRegex.test(newOption.trim())) {
        // Optionally, you could show an error message here
        return;
      }
      const newItem = { name: newOption.trim(), avatar: null };
      setSelected(newItem);
      if (allowDynamicAdd && onAddOption) {
        onAddOption(newItem);
      }
      setNewOption("");
    }
  };

  return (
    <>
      <Listbox as="div" className="w-full flex flex-col" value={selected} onChange={(val) => {
        if (val?.isAddNewAction && onAddNew) {
          onAddNew();
        } else {
          setSelected(val);
        }
      }}>
        {({ open }) => (
          <>
            {title && (
              <Listbox.Label className="block text-sm font-medium capitalize leading-6 text-gray-700 mb-2 text-left">
                {title} {required ? "*" : ""}
              </Listbox.Label>
            )}
            <div className="relative w-full">
              <Listbox.Button className="focus:!border-blue-500 focus:shadow-md transition-all relative w-full cursor-default rounded-lg bg-white py-2.5 pl-3 pr-10 text-left text-gray-900 border border-gray-300 focus:outline-none sm:text-sm sm:leading-6">
                <span className="flex items-center">
                  <span className="block truncate capitalize">{selected?.name || "Select..."}</span>
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
                  <HiArrowsUpDown
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>

              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-50 mt-1 max-h-56 w-full overflow-auto bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {allowDynamicAdd && (
                    <div className="px-2 py-2">
                      <input
                        type="text"
                        onChange={handleInputChange}
                        onKeyDown={handleAddOption}
                        placeholder="Add new option..."
                        className="w-full text-sm text-gray-950 bg-gray-100 border border-gray-300 rounded-lg p-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}
                  {dataArr && dataArr.length>0 && dataArr.map((data, idx) => (
                    <Listbox.Option
                      key={idx}
                      className={({ active }) =>
                        classNames(
                          active ? "bg-teal-600 text-white" : "text-gray-900",
                          "relative z-50 cursor-default select-none py-2 pl-3 pr-9",
                        )
                      }
                      value={data}
                    >
                      {({ selected, active }) => (
                        <>
                          <div className="flex items-center">
                            {withAvatar && (
                              <img
                                src={data?.avatar}
                                alt=""
                                className="h-5 w-5 mr-3 flex-shrink-0 rounded-full"
                              />
                            )}
                            <span
                              className={classNames(
                                selected ? "font-semibold" : "font-normal",
                                "block truncate capitalize",
                              )}
                            >
                              {data?.name}
                            </span>
                          </div>

                          {selected ? (
                            <span
                              className={classNames(
                                active ? "text-white" : "text-teal-600",
                                "absolute inset-y-0 right-0 flex items-center pr-4",
                              )}
                            >
                              <HiOutlineCheck
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                  {onAddNew && (
                    <Listbox.Option
                      key="add-new-action-key"
                      className={({ active }) =>
                        classNames(
                          active ? "bg-blue-600 text-white" : "text-blue-600 bg-blue-50",
                          "relative z-50 cursor-pointer select-none py-2 pl-3 pr-9 font-medium border-t border-gray-200 flex items-center"
                        )
                      }
                      value={{ isAddNewAction: true, name: addLabel || `+ Add New ${title}` }}
                    >
                      {({ active }) => (
                        <div className="flex items-center space-x-2">
                          <HiPlus className="h-4 w-4" />
                          <span>{addLabel || `+ Add New ${title}`}</span>
                        </div>
                      )}
                    </Listbox.Option>
                  )}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox >
    </>
  );
};

export default SelectComponent;